import { useEffect, useState, useCallback, useMemo } from "react";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import { adminService, PaginatedUsersResult, Organisation } from "../services/adminService";
import { UserData } from "../types/user";
import { MagnifyingGlassIcon, XMarkIcon, BuildingOfficeIcon, PencilIcon, CheckIcon, UserGroupIcon, PaperAirplaneIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { DocumentSnapshot, doc, getDoc, updateDoc, collection, query, where, orderBy, getDocs, deleteDoc } from "firebase/firestore";
import { DateTime } from "luxon";
import { Link } from "react-router-dom";
import app, { auth, db, defaultDb, functions } from "../firebase/config";
import { httpsCallable } from "firebase/functions";
import { userService } from "../services/userServices";
import { invitationService } from "../services/invitationService";
import { Invitation } from "../types/invitation";

// Must match PeakPWA/functions/src/demoData/orgTemplate.ts DEMO_ORG_ID — the
// shared PeakElite sales demo organisation.
const DEMO_ORG_ID = 'org_demo_peak_elite';
const COACH_REPORT_TIMEOUT_MS = 60_000;

interface WeeklyCoachReport {
    organisationId: string;
    coachEmails: string[];
    period: string;
    generatedAt: { seconds: number; nanoseconds: number };
    athleteReports: any[];
    _docId?: string;
    _dbName?: "version2" | "default";
}

const CoachesPage: React.FC = () => {
    const [coaches, setCoaches] = useState<(UserData & { uid: string })[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
    const [hasMore, setHasMore] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState<(UserData & { uid: string })[] | null>(null);
    const [searching, setSearching] = useState(false);

    const [coachCount, setCoachCount] = useState<number | null>(null);

    const [organisations, setOrganisations] = useState<Organisation[]>([]);

    const [selectedCoach, setSelectedCoach] = useState<(UserData & { uid: string }) | null>(null);

    const [hasDemoAccess, setHasDemoAccess] = useState(false);
    const [loadingDemoAccess, setLoadingDemoAccess] = useState(false);
    const [togglingDemoAccess, setTogglingDemoAccess] = useState(false);

    // Organization management state
    const [isEditingOrg, setIsEditingOrg] = useState(false);
    const [orgName, setOrgName] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [memberProfiles, setMemberProfiles] = useState<Map<string, UserData>>(new Map());
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [invitations, setInvitations] = useState<Invitation[]>([]);
    const [loadingInvitations, setLoadingInvitations] = useState(false);
    const [processingInvitations, setProcessingInvitations] = useState<Set<string>>(new Set());
    const [creatingOrganisation, setCreatingOrganisation] = useState(false);
    const [newAthleteEmail, setNewAthleteEmail] = useState('');
    const [sendingInvitation, setSendingInvitation] = useState(false);
    const [coachReports, setCoachReports] = useState<WeeklyCoachReport[]>([]);
    const [loadingCoachReports, setLoadingCoachReports] = useState(false);
    const [generatingCoachReport, setGeneratingCoachReport] = useState(false);
    const [selectedCoachReport, setSelectedCoachReport] = useState<WeeklyCoachReport | null>(null);
    const [deletingCoachReport, setDeletingCoachReport] = useState(false);

    // Create a lookup map for organisations by ID
    const orgMap = useMemo(() => {
        const map = new Map<string, Organisation>();
        organisations.forEach(org => map.set(org.id, org));
        return map;
    }, [organisations]);

    // Load initial coaches
    const loadCoaches = useCallback(async () => {
        setLoading(true);
        try {
            const result: PaginatedUsersResult = await adminService.getCoaches(100);
            setCoaches(result.users);
            setLastDoc(result.lastDoc);
            setHasMore(result.hasMore);
        } catch (error) {
            console.error("Error loading coaches:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Load more coaches
    const loadMoreCoaches = async () => {
        if (!hasMore || loadingMore) return;

        setLoadingMore(true);
        try {
            const result = await adminService.getCoaches(20, lastDoc);
            setCoaches((prev) => [...prev, ...result.users]);
            setLastDoc(result.lastDoc);
            setHasMore(result.hasMore);
        } catch (error) {
            console.error("Error loading more coaches:", error);
        } finally {
            setLoadingMore(false);
        }
    };

    // Search coaches
    const handleSearch = async () => {
        const term = searchTerm.trim();
        if (!term) {
            setSearchResults(null);
            return;
        }

        setSearching(true);
        try {
            // Check if it looks like a UID (long alphanumeric string)
            if (term.length >= 20 && /^[a-zA-Z0-9]+$/.test(term)) {
                const coach = await adminService.searchCoachByUid(term);
                setSearchResults(coach ? [coach] : []);
            } else {
                // Search coaches via combined search
                const result = await adminService.searchCoaches(term);
                setSearchResults(result.users);
            }
        } catch (error) {
            console.error("Error searching coaches:", error);
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    };

    // Clear search
    const clearSearch = () => {
        setSearchTerm("");
        setSearchResults(null);
    };

    useEffect(() => {
        document.title = "Coaches - Peak^ Admin";
        loadCoaches();

        const fetchCounts = async () => {
            try {
                const count = await adminService.getCoachCount();
                setCoachCount(count);
            } catch (error) {
                console.error("Error fetching coach count:", error);
            }
        };
        fetchCounts();

        const fetchOrganisations = async () => {
            try {
                const orgs = await adminService.fetchOrganisations();
                setOrganisations(orgs);
            } catch (error) {
                console.error("Error fetching organisations:", error);
            }
        };
        fetchOrganisations();
    }, [loadCoaches]);

    // Get selected coach's organisation
    const selectedCoachOrg = useMemo(() => {
        if (!selectedCoach?.organisationId) return null;
        return orgMap.get(selectedCoach.organisationId) || null;
    }, [selectedCoach, orgMap]);

    // Reset org name when org changes
    useEffect(() => {
        setOrgName(selectedCoachOrg?.name || '');
    }, [selectedCoachOrg]);

    // Fetch member profiles when selected coach's organisation changes
    useEffect(() => {
        const fetchMemberProfiles = async () => {
            if (!selectedCoachOrg || selectedCoachOrg.members.length === 0) {
                setMemberProfiles(new Map());
                return;
            }

            setLoadingMembers(true);

            try {
                const profiles = new Map<string, UserData>();
                const BATCH_SIZE = 10;
                const memberIds = selectedCoachOrg.members.map(m => m.userId);

                for (let i = 0; i < memberIds.length; i += BATCH_SIZE) {
                    const batch = memberIds.slice(i, i + BATCH_SIZE);

                    const batchResults = await Promise.all(
                        batch.map(async (userId) => {
                            try {
                                const userData = await userService.getUserData(userId);
                                return { userId, userData };
                            } catch (error) {
                                console.error(`Error fetching user ${userId}:`, error);
                                return { userId, userData: null };
                            }
                        })
                    );

                    batchResults.forEach(({ userId, userData }) => {
                        if (userData) {
                            profiles.set(userId, userData);
                        }
                    });
                }

                setMemberProfiles(profiles);
            } catch (error) {
                console.error('Error fetching member profiles:', error);
            } finally {
                setLoadingMembers(false);
            }
        };

        fetchMemberProfiles();
    }, [selectedCoachOrg]);

    // Fetch invitations when selected coach changes
    useEffect(() => {
        const fetchInvitations = async () => {
            if (!selectedCoach?.uid) {
                setInvitations([]);
                return;
            }

            setLoadingInvitations(true);
            try {
                const coachInvitations = await invitationService.getInvitationsByCoach(selectedCoach.uid);
                setInvitations(coachInvitations);
            } catch (error) {
                console.error('Error fetching invitations:', error);
            } finally {
                setLoadingInvitations(false);
            }
        };

        fetchInvitations();
    }, [selectedCoach?.uid]);

    // Fetch demo org access status when selected coach changes
    useEffect(() => {
        if (!selectedCoach?.uid) {
            setHasDemoAccess(false);
            return;
        }
        setLoadingDemoAccess(true);
        Promise.all([
            getDoc(doc(db, 'organisations', DEMO_ORG_ID, 'coaches', selectedCoach.uid)),
            getDoc(doc(db, 'organisations', DEMO_ORG_ID, 'owners', selectedCoach.uid)),
        ])
            .then(([coachSnap, ownerSnap]) => setHasDemoAccess(coachSnap.exists() || ownerSnap.exists()))
            .catch((error) => console.error('Error fetching demo access:', error))
            .finally(() => setLoadingDemoAccess(false));
    }, [selectedCoach?.uid]);

    const handleToggleDemoAccess = async () => {
        if (!selectedCoach?.uid) return;
        setTogglingDemoAccess(true);
        try {
            if (hasDemoAccess) {
                const revoke = httpsCallable(functions, 'revokeDemoOrgAccess');
                await revoke({ targetUid: selectedCoach.uid });
                setHasDemoAccess(false);
            } else {
                const grant = httpsCallable(functions, 'grantDemoOrgAccess');
                await grant({ targetUid: selectedCoach.uid, role: 'owner' });
                setHasDemoAccess(true);
            }
        } catch (error) {
            console.error('Error toggling demo access:', error);
        } finally {
            setTogglingDemoAccess(false);
        }
    };

    // Search on Enter key
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleSearch();
        }
    };

    // Format date helper
    const formatDate = (date: Date | undefined) => {
        if (!date) return 'N/A';
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        }).format(date);
    };

    // Save organisation name
    const handleSaveOrganisation = async () => {
        if (!selectedCoachOrg) return;

        setSaving(true);
        setError(null);

        try {
            const orgRef = doc(db, 'organisations', selectedCoachOrg.id);
            await updateDoc(orgRef, {
                name: orgName,
            });

            setIsEditingOrg(false);
            // Refresh organisations
            const orgs = await adminService.fetchOrganisations();
            setOrganisations(orgs);
        } catch (err) {
            console.error('Error updating organisation:', err);
            setError('Failed to update organisation. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // Cancel editing
    const handleCancelEdit = () => {
        setOrgName(selectedCoachOrg?.name || '');
        setIsEditingOrg(false);
        setError(null);
    };

    // Accept invitation
    const handleAcceptInvitation = async (invitation: Invitation) => {
        if (!invitation.id) return;

        setProcessingInvitations(prev => new Set(prev).add(invitation.id!));
        setError(null);

        try {
            const manuallyAddAthlete = httpsCallable(functions, 'manuallyAddAthlete');
            await manuallyAddAthlete({ invitationId: invitation.id });

            // Refresh invitations and organisations
            const updatedInvitations = await invitationService.getInvitationsByCoach(selectedCoach?.uid);
            setInvitations(updatedInvitations);
            const orgs = await adminService.fetchOrganisations();
            setOrganisations(orgs);
        } catch (err: any) {
            console.error('Error accepting invitation:', err);
            setError(err.message || 'Failed to accept invitation. Please try again.');
        } finally {
            setProcessingInvitations(prev => {
                const next = new Set(prev);
                next.delete(invitation.id!);
                return next;
            });
        }
    };

    // Create organisation for coach
    const handleCreateOrganisation = async () => {
        if (!selectedCoach) return;

        setCreatingOrganisation(true);
        setError(null);

        try {
            const createOrganisationViaAdmin = httpsCallable(functions, 'createOrganisationViaAdmin');
            await createOrganisationViaAdmin({ userId: selectedCoach.uid });
            // Refresh organisations
            const orgs = await adminService.fetchOrganisations();
            setOrganisations(orgs);
        } catch (err: any) {
            console.error('Error creating organisation:', err);
            setError(err.message || 'Failed to create organisation. Please try again.');
        } finally {
            setCreatingOrganisation(false);
        }
    };

    // Send invitation to athlete
    const handleSendInvitation = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!newAthleteEmail.trim() || !selectedCoach) return;

        setSendingInvitation(true);
        setError(null);

        try {
            const inviteAthleteViaAdmin = httpsCallable(functions, 'inviteAthleteViaAdmin');
            await inviteAthleteViaAdmin({
                coachId: selectedCoach.uid,
                athleteEmail: newAthleteEmail.trim()
            });

            setNewAthleteEmail('');

            // Refresh invitations
            const updatedInvitations = await invitationService.getInvitationsByCoach(selectedCoach.uid);
            setInvitations(updatedInvitations);
        } catch (err: any) {
            console.error('Error sending invitation:', err);
            setError(err.message || 'Failed to send invitation. Please try again.');
        } finally {
            setSendingInvitation(false);
        }
    };

    // Fetch coach reports for the selected coach's organisation
    const fetchCoachReports = useCallback(async (orgId: string) => {
        setLoadingCoachReports(true);
        setCoachReports([]);
        try {
            const allReports: WeeklyCoachReport[] = [];
            const dbEntries: [typeof db, "version2" | "default"][] = [[db, "version2"], [defaultDb, "default"]];
            for (const [firestore, dbName] of dbEntries) {
                const reportsRef = collection(firestore, `weeklyCoachReports/${orgId}/reports`);
                const snapshot = await getDocs(reportsRef);
                snapshot.docs.forEach((d) => {
                    allReports.push({ ...d.data() as WeeklyCoachReport, _docId: d.id, _dbName: dbName });
                });
                if (allReports.length > 0) break;
            }
            setCoachReports(allReports);
        } catch (error) {
            console.error("Error fetching coach reports:", error);
        } finally {
            setLoadingCoachReports(false);
        }
    }, []);

    // Fetch coach reports when selected coach's org changes
    useEffect(() => {
        if (selectedCoachOrg) {
            fetchCoachReports(selectedCoachOrg.id);
        } else {
            setCoachReports([]);
        }
    }, [selectedCoachOrg, fetchCoachReports]);

    // Generate a coach weekly report
    const handleGenerateCoachReport = async () => {
        if (!selectedCoachOrg) return;

        setGeneratingCoachReport(true);
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), COACH_REPORT_TIMEOUT_MS);
        try {
            const now = DateTime.now();
            const weekId = `${now.weekYear}-W${String(now.weekNumber).padStart(2, "0")}`;
            const projectId = app.options.projectId;
            const token = await auth.currentUser?.getIdToken();

            const response = await fetch(
                `https://us-central1-${projectId}.cloudfunctions.net/weeklyCoachReportGenerator`,
                {
                    method: "POST",
                    signal: controller.signal,
                    headers: {
                        "Content-Type": "application/json",
                        // The function keys its work on the organisation and week, so a repeat is ignored.
                        "Idempotency-Key": `${selectedCoachOrg.id}:${weekId}`,
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify({
                        data: {
                            orgIds: [selectedCoachOrg.id],
                            weekId,
                            batchNumber: 1,
                            totalBatches: 1,
                            sendEmail: true,
                        },
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${await response.text()}`);
            }

            await fetchCoachReports(selectedCoachOrg.id);
        } catch (error) {
            console.error("Error generating coach report:", error);
            if (controller.signal.aborted) {
                alert("The coach report is taking longer than expected. It may still finish in the background; refresh the reports in a few minutes.");
            } else {
                alert("Failed to generate coach report. Check console for details.");
            }
        } finally {
            window.clearTimeout(timeout);
            setGeneratingCoachReport(false);
        }
    };

    // Delete a coach report
    const handleDeleteCoachReport = async (report: WeeklyCoachReport) => {
        if (!report._docId || !report._dbName) return;
        if (!window.confirm(`Delete coach report for ${report.period}? This cannot be undone.`)) return;

        setDeletingCoachReport(true);
        try {
            const firestore = report._dbName === "version2" ? db : defaultDb;
            const reportRef = doc(firestore, `weeklyCoachReports/${report.organisationId}/reports`, report._docId);
            await deleteDoc(reportRef);

            setCoachReports((prev) => prev.filter((r) => r._docId !== report._docId));
            setSelectedCoachReport(null);
        } catch (error) {
            console.error("Error deleting coach report:", error);
            alert("Failed to delete report. Check console for details.");
        } finally {
            setDeletingCoachReport(false);
        }
    };

    const formatReportTimestamp = (ts: { seconds: number; nanoseconds: number } | undefined) => {
        if (!ts) return "N/A";
        return DateTime.fromSeconds(ts.seconds).toLocaleString(DateTime.DATETIME_MED);
    };

    const displayCoaches = searchResults !== null ? searchResults : coaches;

    return (
        <DashboardWrapper>
            <div className="w-full flex h-[calc(100vh-64px)] overflow-hidden">
                {/* Main Content */}
                <div className={`flex-1 flex flex-col gap-6 p-6 overflow-hidden transition-all duration-300 ${selectedCoach ? 'pr-0' : ''}`}>
                    <div className="flex items-center justify-between shrink-0">
                        <h1 className="text-2xl font-heading">Coach Management</h1>
                        <div className="text-sm text-gray-500">
                            {coachCount !== null && `${coachCount.toLocaleString()} total coaches`}
                        </div>
                    </div>

                    {/* Stats Row */}
                    <div className="w-full grid grid-cols-3 gap-2 shrink-0">
                        <div className="w-full bg-white rounded-xl flex flex-col p-4 gap-2 border">
                            <div className="text-2xl text-green-700 font-bold">
                                {coachCount?.toLocaleString() ?? "—"}
                            </div>
                            <div className="text-xs text-gray-500 uppercase">Total Coaches</div>
                        </div>
                        <div className="w-full bg-white rounded-xl flex flex-col p-4 gap-2 border">
                            <div className="text-2xl font-bold">{displayCoaches.length}</div>
                            <div className="text-xs text-gray-500 uppercase">
                                {searchResults !== null ? "Search Results" : "Loaded"}
                            </div>
                        </div>
                        <div className="w-full bg-white rounded-xl flex flex-col p-4 gap-2 border">
                            <div className="text-2xl text-primary font-bold">
                                {displayCoaches.filter(c => c.organisationId).length}
                            </div>
                            <div className="text-xs text-gray-500 uppercase">With Organisation</div>
                        </div>
                    </div>

                    {/* Search Bar */}
                    <div className="w-full flex gap-2 shrink-0">
                        <div className="relative flex-1">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search coaches by username, email, or UID..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={handleKeyDown}
                                className="w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            />
                            {searchTerm && (
                                <button
                                    onClick={clearSearch}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            )}
                        </div>
                        <button
                            onClick={handleSearch}
                            disabled={searching || !searchTerm.trim()}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {searching ? "Searching..." : "Search"}
                        </button>
                        {searchResults !== null && (
                            <button
                                onClick={clearSearch}
                                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                Clear
                            </button>
                        )}
                    </div>

                    {/* Coach List */}
                    <div className="flex-1 min-h-0 bg-white rounded-xl border overflow-hidden flex flex-col">
                        {/* Table Header */}
                        <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase shrink-0">
                            <div className="col-span-1">Avatar</div>
                            <div className="col-span-2">Name</div>
                            <div className="col-span-2">Username</div>
                            <div className="col-span-3">Email</div>
                            <div className="col-span-2">Organisation</div>
                            <div className="col-span-2">UID</div>
                        </div>

                        {/* Table Body */}
                        <div className="flex-1 overflow-y-auto min-h-0">
                            {loading ? (
                                <div className="flex items-center justify-center h-32 text-gray-400">
                                    Loading coaches...
                                </div>
                            ) : displayCoaches.length === 0 ? (
                                <div className="flex items-center justify-center h-32 text-gray-400">
                                    {searchResults !== null
                                        ? "No coaches found matching your search"
                                        : "No coaches found"}
                                </div>
                            ) : (
                                <>
                                    {displayCoaches.map((coach) => (
                                        <div
                                            key={coach.uid}
                                            onClick={() => setSelectedCoach(coach)}
                                            className={`grid grid-cols-12 gap-4 px-4 py-3 border-b hover:bg-gray-50 items-center cursor-pointer transition-colors ${
                                                selectedCoach?.uid === coach.uid ? 'bg-primary/5 border-l-2 border-l-primary' : ''
                                            }`}
                                        >
                                            <div className="col-span-1">
                                                {coach.imageUrl ? (
                                                    <img
                                                        src={coach.imageUrl}
                                                        alt={coach.name || "Coach"}
                                                        className="w-8 h-8 rounded-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-green-800 text-white font-grotesk flex items-center justify-center text-xs font-medium">
                                                        {coach.name?.charAt(0).toUpperCase() || "?"}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="col-span-2 truncate">
                                                <div className="font-medium text-sm capitalize">
                                                    {coach.name} {coach.surname}
                                                </div>
                                            </div>
                                            <div className="col-span-2 text-sm text-gray-600 truncate lowercase">
                                                {coach.username || "—"}
                                            </div>
                                            <div className="col-span-3 text-sm text-gray-600 truncate">
                                                {coach.email || "—"}
                                            </div>
                                            <div className="col-span-2">
                                                {coach.organisationId ? (
                                                    (() => {
                                                        const org = orgMap.get(coach.organisationId);
                                                        return org ? (
                                                            <div className="flex flex-col">
                                                                <span className="text-sm font-medium truncate">{org.name}</span>
                                                                <span className="text-xs text-gray-500">{org.members.length} members</span>
                                                            </div>
                                                        ) : (
                                                            <span className="inline-flex px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 truncate max-w-full">
                                                                {coach.organisationId.substring(0, 8)}...
                                                            </span>
                                                        );
                                                    })()
                                                ) : (
                                                    <span className="text-xs text-gray-400">—</span>
                                                )}
                                            </div>
                                            <div className="col-span-2 text-xs text-gray-400 font-mono truncate">
                                                {coach.uid}
                                            </div>
                                        </div>
                                    ))}

                                    {/* Load More Button */}
                                    {searchResults === null && hasMore && (
                                        <div className="p-4 flex justify-center">
                                            <button
                                                onClick={loadMoreCoaches}
                                                disabled={loadingMore}
                                                className="px-4 py-2 text-sm text-primary border border-primary rounded-lg hover:bg-primary/5 disabled:opacity-50"
                                            >
                                                {loadingMore ? "Loading..." : "Load More"}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Coach Details Sidebar */}
                <div
                    className={`h-full bg-white border-l flex flex-col transition-all duration-300 overflow-hidden ${
                        selectedCoach ? 'w-[400px] ml-6' : 'w-0 ml-0'
                    }`}
                >
                    {selectedCoach && (
                        <>
                            {/* Sidebar Header */}
                            <div className="flex items-start justify-between p-4">
                                <div className="flex items-center gap-4">
                                    {selectedCoach.imageUrl ? (
                                        <img
                                            src={selectedCoach.imageUrl}
                                            alt={selectedCoach.name || "Coach"}
                                            className="w-14 h-14 rounded-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-14 h-14 rounded-full bg-green-600 text-white flex items-center justify-center text-2xl font-medium">
                                            {selectedCoach.name?.charAt(0).toUpperCase() || "?"}
                                        </div>
                                    )}
                                    <div>
                                        <Link to={`/users?user=${selectedCoach.uid}`} className="font-medium font-grotesk hover:underline">
                                            {selectedCoach.name} {selectedCoach.surname}
                                        </Link>
                                        <div className="text-xs font-grotesk text-gray-500 lowercase">@{selectedCoach.username || "—"}</div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSelectedCoach(null)}
                                    className="p-1 hover:bg-gray-100 rounded"
                                >
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Sidebar Content */}
                            <div className="flex-1 border-t overflow-y-auto p-4 space-y-6">


                                {/* Error Display */}
                                {error && (
                                    <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                                        {error}
                                    </div>
                                )}

                                {/* Organisation Section */}
                                {selectedCoachOrg ? (
                                    <div className="space-y-4">
                                        {/* Organisation Header */}
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <BuildingOfficeIcon className="w-5 h-5 text-gray-500" />
                                                <span className="text-xs font-medium text-gray-500 uppercase">Organisation</span>
                                            </div>
                                            {!isEditingOrg ? (
                                                <button
                                                    onClick={() => setIsEditingOrg(true)}
                                                    className="p-1 hover:bg-gray-100 rounded"
                                                >
                                                    <PencilIcon className="w-4 h-4 text-gray-500" />
                                                </button>
                                            ) : (
                                                <div className="flex gap-1">
                                                    <button
                                                        onClick={handleCancelEdit}
                                                        className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                                                        disabled={saving}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={handleSaveOrganisation}
                                                        className="px-2 py-1 text-xs bg-primary text-white rounded flex items-center gap-1"
                                                        disabled={saving}
                                                    >
                                                        {saving ? (
                                                            <span className="animate-spin">...</span>
                                                        ) : (
                                                            <CheckIcon className="w-3 h-3" />
                                                        )}
                                                        Save
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {/* Organisation Name */}
                                        {isEditingOrg ? (
                                            <input
                                                type="text"
                                                value={orgName}
                                                onChange={(e) => setOrgName(e.target.value)}
                                                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                                                placeholder="Organisation name"
                                            />
                                        ) : (
                                            <div>
                                                <div className="font-medium">{selectedCoachOrg.name}</div>
                                                <div className="text-xs text-gray-400 font-mono mt-1">{selectedCoachOrg.id}</div>
                                            </div>
                                        )}

                                        {/* Members Section */}
                                        <div className="border-t pt-4">
                                            <div className="flex items-center gap-2 mb-3">
                                                <UserGroupIcon className="w-4 h-4 text-gray-500" />
                                                <span className="text-xs font-medium text-gray-500 uppercase">
                                                    Members ({selectedCoachOrg.members.length})
                                                </span>
                                            </div>

                                            {loadingMembers ? (
                                                <div className="flex justify-center py-4">
                                                    <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full"></div>
                                                </div>
                                            ) : selectedCoachOrg.members.length > 0 ? (
                                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                                    {selectedCoachOrg.members.map((member) => {
                                                        const profile = memberProfiles.get(member.userId);
                                                        return (
                                                            <div
                                                                key={member.userId}
                                                                className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
                                                            >
                                                                {profile?.imageUrl ? (
                                                                    <img
                                                                        src={profile.imageUrl}
                                                                        alt={profile.name || "Member"}
                                                                        className="w-8 h-8 rounded-full object-cover"
                                                                    />
                                                                ) : (
                                                                    <div className="w-8 h-8 rounded-full bg-gray-300 text-white flex items-center justify-center text-xs font-medium">
                                                                        {profile?.name?.charAt(0).toUpperCase() || "?"}
                                                                    </div>
                                                                )}
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="text-sm font-medium truncate">
                                                                        {profile ? `${profile.name} ${profile.surname}` : member.userId}
                                                                    </div>
                                                                    <div className="text-xs text-gray-400 truncate">
                                                                        {profile?.email || member.userId}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className="text-center py-4 text-gray-400 text-sm">
                                                    No members
                                                </div>
                                            )}
                                        </div>

                                        {/* Invitations Section */}
                                        <div className="border-t pt-4">
                                            <div className="flex items-center gap-2 mb-3">
                                                <PaperAirplaneIcon className="w-4 h-4 text-gray-500" />
                                                <span className="text-xs font-medium text-gray-500 uppercase">
                                                    Invitations ({invitations.length})
                                                </span>
                                            </div>

                                            {/* Send Invitation Form */}
                                            <form onSubmit={handleSendInvitation} className="flex gap-2 mb-3">
                                                <input
                                                    type="email"
                                                    value={newAthleteEmail}
                                                    onChange={(e) => setNewAthleteEmail(e.target.value)}
                                                    placeholder="Athlete email"
                                                    className="flex-1 px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                                                    disabled={sendingInvitation}
                                                />
                                                <button
                                                    type="submit"
                                                    disabled={sendingInvitation || !newAthleteEmail.trim()}
                                                    className="px-3 py-1.5 bg-primary text-white rounded-lg text-sm flex items-center gap-1 disabled:opacity-50"
                                                >
                                                    {sendingInvitation ? (
                                                        <span className="animate-spin">...</span>
                                                    ) : (
                                                        <PaperAirplaneIcon className="w-4 h-4" />
                                                    )}
                                                    Send
                                                </button>
                                            </form>

                                            {loadingInvitations ? (
                                                <div className="flex justify-center py-4">
                                                    <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full"></div>
                                                </div>
                                            ) : invitations.length > 0 ? (
                                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                                    {invitations.map((invitation) => {
                                                        const isProcessing = processingInvitations.has(invitation.id || '');
                                                        const isPending = invitation.status === 'pending';

                                                        return (
                                                            <div
                                                                key={invitation.id}
                                                                className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                                                            >
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="text-sm font-medium truncate">
                                                                        {invitation.athleteEmail}
                                                                    </div>
                                                                    <div className="flex items-center gap-2 mt-0.5">
                                                                        <span className="text-xs text-gray-400">
                                                                            {formatDate(invitation.createdAt)}
                                                                        </span>
                                                                        <span
                                                                            className={`px-1.5 py-0.5 rounded text-[10px] uppercase ${
                                                                                invitation.status === 'accepted'
                                                                                    ? 'bg-green-100 text-green-600'
                                                                                    : invitation.status === 'pending'
                                                                                    ? 'bg-yellow-100 text-yellow-600'
                                                                                    : invitation.status === 'expired'
                                                                                    ? 'bg-orange-100 text-orange-600'
                                                                                    : 'bg-gray-100 text-gray-600'
                                                                            }`}
                                                                        >
                                                                            {invitation.status}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                {isPending && (
                                                                    <button
                                                                        onClick={() => handleAcceptInvitation(invitation)}
                                                                        disabled={isProcessing}
                                                                        className="ml-2 p-1.5 bg-primary text-white rounded text-xs flex items-center gap-1 disabled:opacity-50"
                                                                        title="Manually accept invitation"
                                                                    >
                                                                        {isProcessing ? (
                                                                            <span className="animate-spin">...</span>
                                                                        ) : (
                                                                            <CheckCircleIcon className="w-4 h-4" />
                                                                        )}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className="text-center py-4 text-gray-400 text-sm">
                                                    No invitations sent
                                                </div>
                                            )}
                                        </div>

                                        {/* Coach Reports Section */}
                                        <div className="border-t pt-4">
                                            <div className="flex items-center gap-2 mb-3">
                                                <span className="text-xs font-medium text-gray-500 uppercase">
                                                    Coach Weekly Reports
                                                </span>
                                            </div>
                                            <button
                                                onClick={handleGenerateCoachReport}
                                                disabled={generatingCoachReport}
                                                className="w-full px-4 py-2 bg-primary text-white rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-50 mb-3"
                                            >
                                                {generatingCoachReport ? "Generating..." : "Generate Coach Weekly Report"}
                                            </button>
                                            {loadingCoachReports ? (
                                                <p className="text-xs text-gray-400">Loading reports...</p>
                                            ) : coachReports.length === 0 ? (
                                                <p className="text-xs text-gray-400">No reports found</p>
                                            ) : (
                                                <div className="flex flex-col gap-1">
                                                    {coachReports.map((report, idx) => (
                                                        <button
                                                            key={idx}
                                                            onClick={() => setSelectedCoachReport(report)}
                                                            className="w-full text-left px-3 py-2 rounded-lg border hover:bg-gray-50 transition-colors"
                                                        >
                                                            <div className="text-sm font-medium">{report.period}</div>
                                                            <div className="text-xs text-gray-500">
                                                                {formatReportTimestamp(report.generatedAt)}
                                                                {report.athleteReports?.length !== undefined && (
                                                                    <span className="ml-2">{report.athleteReports.length} athletes</span>
                                                                )}
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2">
                                            <BuildingOfficeIcon className="w-5 h-5 text-gray-500" />
                                            <span className="text-xs font-medium text-gray-500 uppercase">Organisation</span>
                                        </div>
                                        <p className="text-sm text-gray-500">
                                            This coach is not associated with any organisation.
                                        </p>
                                        <button
                                            onClick={handleCreateOrganisation}
                                            disabled={creatingOrganisation}
                                            className="w-full px-4 py-2 bg-primary text-white rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                                        >
                                            {creatingOrganisation ? (
                                                <span className="animate-spin">...</span>
                                            ) : (
                                                <BuildingOfficeIcon className="w-4 h-4" />
                                            )}
                                            Create Organisation
                                        </button>
                                    </div>
                                )}

                                {/* Demo Access Section */}
                                <div className="border-t pt-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <UserGroupIcon className="w-4 h-4 text-gray-500" />
                                        <span className="text-xs font-medium text-gray-500 uppercase">Demo Access</span>
                                    </div>
                                    {loadingDemoAccess ? (
                                        <div className="flex justify-center py-2">
                                            <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full"></div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                                            <div>
                                                <p className="text-sm font-medium">PeakElite Sales Demo Org</p>
                                                <p className="text-xs text-gray-400 mt-0.5">
                                                    {hasDemoAccess ? 'Can switch into the demo org in PeakElite' : 'No access'}
                                                </p>
                                            </div>
                                            <button
                                                onClick={handleToggleDemoAccess}
                                                disabled={togglingDemoAccess}
                                                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed ${hasDemoAccess ? 'bg-gray-800' : 'bg-gray-200'}`}
                                            >
                                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${hasDemoAccess ? 'translate-x-6' : 'translate-x-1'}`} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Coach Report Detail Modal */}
                {selectedCoachReport && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedCoachReport(null)}>
                        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between p-4 border-b">
                                <div>
                                    <h2 className="text-lg font-semibold">Coach Weekly Report</h2>
                                    <p className="text-sm text-gray-500">{selectedCoachReport.period}</p>
                                </div>
                                <button
                                    onClick={() => setSelectedCoachReport(null)}
                                    className="p-1 hover:bg-gray-100 rounded"
                                >
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4">
                                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm whitespace-pre-wrap">
                                    {JSON.stringify(selectedCoachReport, null, 2)}
                                </pre>
                            </div>
                            <div className="flex justify-end p-4 border-t">
                                <button
                                    onClick={() => handleDeleteCoachReport(selectedCoachReport)}
                                    disabled={deletingCoachReport}
                                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {deletingCoachReport ? "Deleting..." : "Delete Report"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardWrapper>
    );
};

// Helper component for detail rows
const DetailRow: React.FC<{ label: string; value?: string | null; mono?: boolean }> = ({ label, value, mono }) => (
    <div className="flex flex-col gap-1 w-full">
        <div className="text-xs font-medium text-gray-500 uppercase">{label}</div>
        <div className={`text-sm break-words ${mono ? 'font-mono text-xs' : ''} ${value ? 'text-gray-900' : 'text-gray-400'}`}>
            {value || "—"}
        </div>
    </div>
);

export default CoachesPage;
